import type { StorySymbol, BBModel, BBModelChild, BBSymbolInstance, ComponentTypes } from 'types/story';
import { set, unset } from 'lodash';
import { produce, type Draft } from 'immer';
import { deepClone } from 'utils/helpers';
import { componentWalk } from 'utils/blocks/component-walk';
import { generateElementId, generateNodeId, updateId } from 'utils/generate-id';

export type GenerateIdCallback = (p: { oldId: string; newId: string; type: ComponentTypes }) => void;

/**
 * Change existed element model to master symbol model
 *
 * @return new symbol master
 */
type TransformElementToSymbolMaster = (originalElement: BBModel) => StorySymbol['master'];

/**
 * change existed element model to a symbol instance model using master symbol
 */
type TransformElementToSymbolInstance = (element: BBModel) => BBSymbolInstance;

/**
 * Create a new instance from an element using master symbol (Add symbol)
 *
 * @return new symbol instance
 */
type CreateInstanceFromMasterSymbol = (generateIdCallback?: GenerateIdCallback) => BBSymbolInstance;

/**
 * Change exited element model to become a symbol child
 *
 * @return transformed element
 */
type TransformToInstanceChild = (props: {
	element: BBModel;
	masterSymbol: BBModel;
	masterId: string;
	instanceId: string;
}) => BBModel;

type CreateInstanceFromInstance = (
	instance: BBSymbolInstance,
	generateIdCallback?: GenerateIdCallback
) => BBSymbolInstance;

/**
 * Create link to symbol instance
 *
 * @returns symbol link
 */
type CreateInstanceLink = (instance: BBModel) => {
	symbol: { masterId: string; instanceId: string };
};

type Transform = (p: {
	element: Draft<BBModel>;
	masterSymbol: StorySymbol['master'];
	masterId?: string;
	isNew?: boolean;
	instanceId?: string;
	generateIdCallback?: GenerateIdCallback;
}) => BBModel;

type ErrorConstructor<T extends Error = Error> = new (message: string) => T;

export class SymbolManager {
	symbol?: StorySymbol;

	private readonly noSymbolErr = 'Symbol is missed in constructor';

	private readonly ErrorClass: ErrorConstructor;

	constructor(props: { symbol?: StorySymbol; errorClass?: ErrorConstructor } = {}) {
		this.symbol = props.symbol;
		this.ErrorClass = props.errorClass || Error;
	}

	transformElementToSymbolMaster: TransformElementToSymbolMaster = originalElement => {
		return produce(originalElement, draftElement => {
			componentWalk<BBModelChild>([draftElement], function setupMasterComponents({ component, path }) {
				if (!('_id' in component)) return;
				unset(component, 'uiConfig.nodeProps.id');
				unset(component, 'uiConfig.layer');

				componentWalk(component.children, setupMasterComponents);
			});
		});
	};

	transformElementToSymbolInstance: TransformElementToSymbolInstance = element => {
		if (!this.symbol) throw new this.ErrorClass(this.noSymbolErr);
		const masterSymbol = this.symbol.master;

		return produce(element, draft => {
			this.transform({ element: draft, masterSymbol, instanceId: element._id });
		}) as BBSymbolInstance;
	};

	createInstanceFromMasterSymbol: CreateInstanceFromMasterSymbol = generateIdCallback => {
		if (!this.symbol) throw new this.ErrorClass(this.noSymbolErr);
		const masterSymbol = this.symbol.master;

		return produce<BBModel>(this.symbol.master, draft => {
			this.transform({ element: draft, masterSymbol, isNew: true, generateIdCallback });
		}) as BBSymbolInstance;
	};

	transformToInstanceChild: TransformToInstanceChild = ({ element, masterSymbol, masterId, instanceId }) => {
		return produce<BBModel>(element, draft => {
			this.transform({ element: draft, masterSymbol, isNew: true, instanceId, masterId });
		});
	};

	createInstanceLink: CreateInstanceLink = instance => {
		if (!this.symbol) throw new this.ErrorClass(this.noSymbolErr);

		return { symbol: { masterId: this.symbol.master._id, instanceId: instance._id } };
	};

	createInstanceFromInstance: CreateInstanceFromInstance = (instance, generateIdCallback) => {
		const instanceId = generateElementId();
		const clone = deepClone(instance, 'json');

		// collect data for demand
		generateIdCallback?.({
			oldId: clone._id,
			newId: instanceId,
			type: clone.type,
		});

		// update
		set(clone, '_id', instanceId);
		set(clone, 'uiConfig.nodeProps.id', generateNodeId(instanceId, clone.type));
		set(clone, 'symbol.instanceId', instanceId);

		componentWalk(clone.children, function modifyInstanceChild({ component }) {
			if (!('_id' in component)) return;
			const _id = generateElementId();

			// collect data for demand
			generateIdCallback?.({
				oldId: component._id,
				newId: _id,
				type: component.type,
			});

			// update
			set(component, '_id', _id);
			set(component, 'uiConfig.nodeProps.id', generateNodeId(_id, component.type));
			set(component, 'symbol.instanceId', instanceId);
			// component.symbol.masterId --> masterId links to the same master symbol id
			// component.symbol.childId --> childId links to the same child id in master symbol

			componentWalk(component.children, modifyInstanceChild);
		});

		return clone;
	};

	// Main function to transform elements into symbols
	transform: Transform = ({
		element: el,
		masterSymbol,
		masterId: masterIdProp,
		isNew,
		instanceId,
		generateIdCallback,
	}) => {
		set(el, 'uiConfig.editorProps', {});
		set(el, 'uiConfig.componentProps', {});
		// set(element, 'uiConfig.nodeProps', {});

		if (isNew) {
			const oldId = el._id;
			const newId = updateId(el);
			generateIdCallback?.({ type: el.type, oldId, newId });
		}

		const masterId = masterIdProp || masterSymbol._id; // always link to root symbol
		const iId = instanceId || el._id;
		const links = { masterId, instanceId: iId, ...(masterIdProp && { childId: masterSymbol._id }) };

		set(el, 'symbol', links);

		if (Array.isArray(el.children)) {
			el.children.forEach((e, i) => {
				if (!('_id' in e)) return;
				this.transform({
					element: e,
					masterSymbol: masterSymbol.children[i],
					masterId,
					isNew,
					instanceId: iId,
					generateIdCallback,
				});
			});

			return el;
		}

		// Clear text node children
		set(el, 'children', {});

		return el;
	};
}
